import React, { useMemo } from 'react';
import { Cluster } from '../../../utils/clustering';
import { transformPoints, RotationAngle } from '../utils';

interface TeamShapePlotProps {
  clusters: Cluster[];
  rotation?: RotationAngle;
  scale?: number;
  teamColor?: string;
  minSize?: number; // Minimum circle size
  maxSize?: number; // Maximum circle size
  opacity?: number; // Circle opacity
  sizeRange?: { min: number; max: number }; // Optional shared size range (for comparing multiple teams)
}

/**
 * Team Shape Plot visualization component
 * Renders clusters as circles at average player positions
 * Circle size represents event involvement (number of events)
 */
const TeamShapePlot: React.FC<TeamShapePlotProps> = ({
  clusters,
  rotation = 0,
  scale = 1.0,
  teamColor = '#3B82F6',
  minSize = 12,
  maxSize = 40,
  opacity = 0.7,
  sizeRange: providedSizeRange,
}) => {
  // Calculate size range for normalization using totalWeight (better represents involvement)
  // Use provided size range if available (for comparing multiple teams), otherwise calculate from clusters
  const sizeRange = useMemo(() => {
    if (providedSizeRange) {
      return providedSizeRange;
    }
    
    if (clusters.length === 0) {
      return { min: 0, max: 0 };
    }
    
    const weights = clusters.map(c => c.totalWeight);
    const min = Math.min(...weights);
    const max = Math.max(...weights);
    
    return { min, max };
  }, [clusters, providedSizeRange]);

  // Normalize totalWeight to circle size using power scaling for better visual distinction
  // Using power of 0.4 (between square root 0.5 and cube root 0.33) for more aggressive scaling
  const getCircleSize = (totalWeight: number): number => {
    if (sizeRange.max === sizeRange.min) {
      return (minSize + maxSize) / 2;
    }
    
    // Use power scaling (0.4) to make differences more visible
    // This gives more visual distinction between clusters than square root
    const normalized = (totalWeight - sizeRange.min) / (sizeRange.max - sizeRange.min);
    const powerNormalized = Math.pow(normalized, 0.4); // Power of 0.4 for better visual distinction
    return minSize + powerNormalized * (maxSize - minSize);
  };

  // Transform cluster centroids based on rotation
  const transformedClusters = useMemo(() => {
    return clusters.map(cluster => {
      const transformed = transformPoints([cluster.centroid], rotation, scale);
      return {
        ...cluster,
        transformedCentroid: transformed[0]
      };
    });
  }, [clusters, rotation, scale]);

  if (clusters.length === 0) {
    return null;
  }

  return (
    <g id="team-shape-plot">
      {transformedClusters.map((cluster, index) => {
        const size = getCircleSize(cluster.totalWeight);
        const { x, y } = cluster.transformedCentroid;
        
        return (
          <g key={`cluster-${index}`}>
            {/* Circle representing player position */}
            <circle
              cx={x}
              cy={y}
              r={size}
              fill={teamColor}
              opacity={opacity}
              stroke="#ffffff"
              strokeWidth={2}
              className="team-shape-cluster"
            />
            {/* Optional: Show event count as text (can be toggled) */}
            {/* <text
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={size * 0.6}
              fill="#ffffff"
              fontWeight="bold"
              className="team-shape-count"
            >
              {cluster.eventCount}
            </text> */}
          </g>
        );
      })}
    </g>
  );
};

export default TeamShapePlot;

